import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import { EmailTemplateService } from 'service/emailTemplateService';
import { EmailService } from 'service/emailService';

const STEPS = ['Select Template', 'Review Audience', 'Send Campaign'];

export default function BulkMarketingEmailDialog({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [search, setSearch] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const marketingTemplates = useMemo(
    () => templates.filter((t) => ['promotions', 'sale'].includes(String(t.communicationType || '').toLowerCase())),
    [templates]
  );

  const resetState = () => {
    setActiveStep(0);
    setSelectedTemplateId('');
    setSearch('');
    setPreview(null);
    setResult(null);
    setError('');
    setPreviewLoading(false);
    setSending(false);
  };

  const handleClose = () => {
    if (sending) {
      return;
    }
    resetState();
    onClose();
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    setError('');
    try {
      const response = await EmailTemplateService.getTemplates();
      setTemplates(response?.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  React.useEffect(() => {
    if (!open) {
      return;
    }
    loadTemplates();
  }, [open]);

  const handlePreview = async () => {
    if (!selectedTemplateId) {
      setError('Please select a marketing template.');
      return;
    }

    setPreviewLoading(true);
    setError('');
    try {
      const response = await EmailService.previewMarketingCampaign({
        templateId: selectedTemplateId,
        search,
        limit: 500
      });
      setPreview(response?.data || null);
      setActiveStep(1);
    } catch (e) {
      setError(e.message || 'Failed to preview campaign audience');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplateId) {
      setError('Please select a marketing template.');
      return;
    }

    setSending(true);
    setError('');
    try {
      const response = await EmailService.sendMarketingCampaign({
        templateId: selectedTemplateId,
        search,
        limit: 500
      });
      setResult(response?.data || null);
      setActiveStep(2);
      toast.success('Marketing campaign processed.');
    } catch (e) {
      setError(e.message || 'Failed to send marketing campaign');
    } finally {
      setSending(false);
    }
  };

  const renderStepSelectTemplate = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Select a Promotions/Sale template and optionally filter customers by name or email.
      </Typography>
      <FormControl fullWidth>
        <InputLabel>Marketing Template</InputLabel>
        <Select
          value={selectedTemplateId}
          label="Marketing Template"
          onChange={(e) => setSelectedTemplateId(e.target.value)}
          disabled={loadingTemplates}
        >
          <MenuItem value="">
            <em>-- Choose a template --</em>
          </MenuItem>
          {marketingTemplates.map((template) => (
            <MenuItem key={template.id} value={template.id}>
              {template.templateName} ({template.communicationType})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Search Customers (Optional)"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by first name, last name, or email"
      />

      {loadingTemplates && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading templates...</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handlePreview} disabled={previewLoading || loadingTemplates}>
          {previewLoading ? 'Previewing...' : 'Preview Audience'}
        </Button>
      </Box>
    </Stack>
  );

  const renderStepReview = () => (
    <Stack spacing={2}>
      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="h6">{preview?.counts?.totalFound || 0}</Typography>
            <Typography variant="caption">Total Found</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h6" color="success.main">{preview?.counts?.eligible || 0}</Typography>
            <Typography variant="caption">Eligible (Opted In)</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="h6" color="warning.main">{preview?.counts?.ineligible || 0}</Typography>
            <Typography variant="caption">Ineligible</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Customer</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(preview?.audience || []).slice(0, 100).map((row) => (
            <TableRow key={`${row.customerId}-${row.email}`}>
              <TableCell>{row.fullName || 'Unknown'}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.optedIn ? 'Eligible' : 'Opted Out'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Alert severity="info">
        Showing up to first 100 recipients in preview. Send action processes up to 500 recipients.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Button onClick={() => setActiveStep(0)}>Back</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSend} disabled={sending || !preview?.counts?.eligible}>
            {sending ? 'Sending...' : 'Send Campaign'}
          </Button>
        </Box>
      </Box>
    </Stack>
  );

  const renderStepResult = () => (
    <Stack spacing={2}>
      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="h6">{result?.summary?.totalFound || 0}</Typography>
            <Typography variant="caption">Total Found</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h6" color="success.main">{result?.summary?.sent || 0}</Typography>
            <Typography variant="caption">Sent</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h6" color="warning.main">{result?.summary?.blocked || 0}</Typography>
            <Typography variant="caption">Blocked</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="h6" color="error.main">{result?.summary?.failed || 0}</Typography>
            <Typography variant="caption">Failed</Typography>
          </Grid>
        </Grid>
      </Paper>

      {!!(result?.failed || []).length && (
        <Alert severity="error">
          {result.failed.length} recipient(s) failed. Please review backend logs for details.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={handleClose}>Close</Button>
      </Box>
    </Stack>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Send Marketing Email Campaign</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((step) => (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {activeStep === 0 && renderStepSelectTemplate()}
        {activeStep === 1 && renderStepReview()}
        {activeStep === 2 && renderStepResult()}
      </DialogContent>
    </Dialog>
  );
}
